/**
 * Campaign analytics — a modular, registry-driven rollup layer.
 *
 * Two things live here:
 *
 *  1. **Pure aggregation functions** that roll the generic delivery/campaign CRUD rows up into the
 *     flat `{ label, value }` row arrays the dashboard tile renderer consumes (see
 *     [[tile-renderer-inline-data]]). Framework-free and independently unit-testable.
 *
 *  2. **A tile registry** (`AnalyticsTileSpec` + `registerCampaignAnalytics`) so the set of
 *     analytics widgets is *data*, not hard-coded JSX. A base set of campaign-agnostic tiles
 *     (deliveries, status, completion, trend) applies to every campaign; any future campaign type
 *     registers its own tiles keyed by `runner_key` — the exact analog of the transformation op
 *     registry (`support/transformations/registry.ts`), the content-block schema registry, and the
 *     runner registry. This is what keeps analytics working for "the first campaign, not the only
 *     one": a new campaign kind adds a registration, never an edit to the dashboard.
 *
 * When the server-side `campaign-analytics` rollup endpoints land (plan workstream B), only the
 * `AnalyticsContext` data source swaps — every registered tile keeps working against the same shape.
 */
import {
  Campaign,
  CampaignActionItem,
  CampaignDelivery,
  CampaignDeliveryStepStatus,
  CampaignDeliveryTarget,
  CampaignEnrollment,
  CampaignStep,
  DELIVERY_STATUSES,
  DeliveryStatus,
  EnrollmentStatus,
  STEP_STATUSES,
  StepStatus,
  actionItemStatusLabel,
  deliveryStatusLabel,
  enrollmentStatus,
  statusLabel,
  stepKindLabel,
  stepStatusLabel,
  visibleSteps,
} from './campaigns';

// ---------------------------------------------------------------------------
// Tile-ready shapes
// ---------------------------------------------------------------------------

/**
 * A flat, tile-ready row. `label` is the category; `value` is the number a chart plots.
 * `display` is an optional pre-formatted string a KPI card shows instead of the raw `value`
 * (e.g. a compact-USD pipeline total) — charts still plot the numeric `value`, so this stays
 * a numeric row for every non-KPI consumer.
 */
export interface MetricRow {
  label: string;
  value: number;
  display?: string;
}

/** The chart types a registered analytics tile may request (mirrors the dashboard chart catalogue). */
export type AnalyticsChartType =
  | 'horizontal_bar_chart'
  | 'vertical_bar_chart'
  | 'donut_chart'
  | 'pie_chart'
  | 'gauge_chart'
  | 'line_chart'
  | 'datagrid'
  | 'metricchart';

/** The field names every analytics chart tile points its `labelField`/`valueField` settings at. */
export const ANALYTICS_LABEL_FIELD = 'label';
export const ANALYTICS_VALUE_FIELD = 'value';

/** Default chart settings for a `{ label, value }` tile. */
export const DEFAULT_CHART_SETTINGS = {
  labelField: ANALYTICS_LABEL_FIELD,
  valueField: ANALYTICS_VALUE_FIELD,
};

// ---------------------------------------------------------------------------
// The context a tile computes against — extend with new named sources over time
// ---------------------------------------------------------------------------

/**
 * Everything a tile's `compute` may read. `campaigns`/`deliveries` are the always-present base;
 * `extra` is an open, forward-compatible bag so a campaign type can register a tile that needs a
 * source not yet promoted to a named field (enrollments, targets, step statuses, outcomes, …) —
 * the analytics host fetches and supplies it. Scoping (`campaignId`) is present when the dashboard
 * is a single campaign's analytics (plan D2) rather than the whole program.
 */
export interface AnalyticsContext {
  campaigns: Campaign[];
  deliveries: CampaignDelivery[];
  /** Set when the dashboard is scoped to a single campaign; undefined for program-wide. */
  campaignId?: number;
  /** Named, lazily-provided extra data sources keyed by a stable string (e.g. `'enrollments'`). */
  extra?: Record<string, any[]>;
}

// ---------------------------------------------------------------------------
// Registry types
// ---------------------------------------------------------------------------

export type AnalyticsScope = 'program' | 'campaign' | 'delivery';

/** A single registered analytics widget: what to compute and how to draw it. */
export interface AnalyticsTileSpec {
  /** Stable unique id across all registrations (used as the tile key / synthetic tile id seed). */
  key: string;
  title: string;
  chartType: AnalyticsChartType;
  /** Produce the flat rows this tile plots from the context. */
  compute: (ctx: AnalyticsContext) => any[];
  /** Tile settings; defaults to `{ labelField, valueField }`. Return `{ selectedColumns }` for tables. */
  settings?: (ctx: AnalyticsContext) => Record<string, any>;
  /** Rendered body height in px. */
  height?: number;
  /** Layout hint: `full` spans the row, `half` sits in a responsive 2-up grid. Default `half`. */
  span?: 'full' | 'half';
  /** Hide the tile unless this returns true (e.g. no data, or a runner-specific precondition). */
  visible?: (ctx: AnalyticsContext) => boolean;
}

/** A named group of tiles, optionally restricted by scope and/or campaign runner. */
export interface AnalyticsSection {
  key: string;
  title?: string;
  /** Which dashboard(s) this section belongs to. Default: both. */
  scopes?: AnalyticsScope[];
  /**
   * Restrict to campaigns whose `runner_key` is in this list. Undefined = applies to every campaign.
   * In program scope a section shows if ANY campaign in context matches; in campaign scope it shows
   * only for a matching campaign. This is the per-campaign-type extension seam.
   */
  runnerKeys?: string[];
  tiles: AnalyticsTileSpec[];
}

// ---------------------------------------------------------------------------
// Base aggregation functions (campaign-agnostic — apply to every campaign)
// ---------------------------------------------------------------------------

const STATUS_LABEL = new Map<DeliveryStatus, string>(
  DELIVERY_STATUSES.map((s) => [s.value, s.label])
);

/** Completion = completed ÷ (deliveries that are not cancelled), as a whole-number percent. */
export function completionPct(deliveries: CampaignDelivery[]): number {
  const denom = deliveries.filter((d) => d.status !== 'cancelled').length;
  if (denom <= 0) return 0;
  const completed = deliveries.filter((d) => d.status === 'completed').length;
  return Math.round((completed / denom) * 100);
}

/** Count of deliveries in each status, in the canonical status order. Empty statuses stay at 0. */
export function statusDistribution(deliveries: CampaignDelivery[]): MetricRow[] {
  const counts = new Map<DeliveryStatus, number>(DELIVERY_STATUSES.map((s) => [s.value, 0]));
  deliveries.forEach((d) => {
    if (counts.has(d.status)) counts.set(d.status, (counts.get(d.status) ?? 0) + 1);
  });
  return DELIVERY_STATUSES.map((s) => ({ label: s.label, value: counts.get(s.value) ?? 0 }));
}

/** Headline scalars for the KPI stat-tile row. */
export function programKpis(campaigns: Campaign[], deliveries: CampaignDelivery[]): MetricRow[] {
  const active = deliveries.filter(
    (d) => d.status === 'scheduled' || d.status === 'in_progress'
  ).length;
  const completed = deliveries.filter((d) => d.status === 'completed').length;
  return [
    { label: 'Campaigns', value: campaigns.length },
    { label: 'Deliveries', value: deliveries.length },
    { label: 'Active', value: active },
    { label: 'Completed', value: completed },
    { label: 'Completion %', value: completionPct(deliveries) },
  ];
}

/** Column order for the per-campaign datagrid tile. */
export const PER_CAMPAIGN_COLUMNS: string[] = [
  'Campaign',
  'Status',
  'Deliveries',
  ...DELIVERY_STATUSES.map((s) => s.label),
  'Completion %',
];

/** One datagrid row per campaign: name, status counts, and completion — the tiled Program Rollup. */
export function perCampaignRows(
  campaigns: Campaign[],
  deliveries: CampaignDelivery[]
): Record<string, string | number>[] {
  const byCampaign = new Map<number, CampaignDelivery[]>();
  deliveries.forEach((d) => {
    const list = byCampaign.get(d.campaign) ?? [];
    list.push(d);
    byCampaign.set(d.campaign, list);
  });

  return campaigns.map((c) => {
    const rows = byCampaign.get(c.id) ?? [];
    const row: Record<string, string | number> = {
      Campaign: c.name,
      Status: c.status,
      Deliveries: rows.length,
    };
    DELIVERY_STATUSES.forEach((s) => {
      row[STATUS_LABEL.get(s.value) as string] = rows.filter((d) => d.status === s.value).length;
    });
    row['Completion %'] = completionPct(rows);
    return row;
  });
}

/**
 * Deliveries created per calendar month (YYYY-MM), oldest first — the trend line's data. Rows
 * without a parseable `created` timestamp are skipped; months with no deliveries are omitted.
 */
export function deliveryTrend(deliveries: CampaignDelivery[]): MetricRow[] {
  const perMonth = new Map<string, number>();
  deliveries.forEach((d) => {
    if (!d.created) return;
    const t = new Date(d.created);
    if (Number.isNaN(t.getTime())) return;
    const key = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
    perMonth.set(key, (perMonth.get(key) ?? 0) + 1);
  });
  return Array.from(perMonth.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([label, value]) => ({ label, value }));
}

// ---------------------------------------------------------------------------
// Per-user (enrollment) aggregation — the enablement side of a campaign
// ---------------------------------------------------------------------------

/** Short, locale-formatted date, or an em-dash for empty/unparseable timestamps. */
export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** An enrollment's completion percent — the server-computed rollup, or a coarse status fallback. */
export function enrollmentPercent(e: CampaignEnrollment): number {
  if (e.progress && typeof e.progress.percent === 'number') return Math.round(e.progress.percent);
  return e.status === 'completed' ? 100 : 0;
}

const ENROLLMENT_STATUS_ORDER: { value: EnrollmentStatus; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

/** Headline scalars for a single campaign: enablement (enrollments) + delivery blended. */
export function campaignKpis(
  enrollments: CampaignEnrollment[],
  deliveries: CampaignDelivery[]
): MetricRow[] {
  const enrolled = enrollments.length;
  // Derived, composite-aware rollup — not the stale stored `status` column.
  const completed = enrollments.filter((e) => enrollmentStatus(e) === 'completed').length;
  const inProgress = enrollments.filter((e) => enrollmentStatus(e) === 'in_progress').length;
  const avg = enrolled
    ? Math.round(enrollments.reduce((sum, e) => sum + enrollmentPercent(e), 0) / enrolled)
    : 0;
  return [
    { label: 'Enrolled', value: enrolled },
    { label: 'In progress', value: inProgress },
    { label: 'Completed', value: completed },
    { label: 'Avg progress %', value: avg },
    { label: 'Deliveries', value: deliveries.length },
    { label: 'Delivery completion %', value: completionPct(deliveries) },
  ];
}

/** Count of enrollments in each status, canonical order — the learner-status donut/bar. */
export function enrollmentStatusDistribution(enrollments: CampaignEnrollment[]): MetricRow[] {
  const counts = new Map<EnrollmentStatus, number>(ENROLLMENT_STATUS_ORDER.map((s) => [s.value, 0]));
  enrollments.forEach((e) => {
    if (counts.has(e.status)) counts.set(e.status, (counts.get(e.status) ?? 0) + 1);
  });
  return ENROLLMENT_STATUS_ORDER.map((s) => ({ label: s.label, value: counts.get(s.value) ?? 0 }));
}

/** Column order for the per-user enrollment datagrid. */
export const ENROLLMENT_COLUMNS: string[] = [
  'User',
  'Status',
  'Progress %',
  'Steps done',
  'Journey maps',
  'Assessments',
  'Account',
  'Opportunity',
  'Started',
  'Completed',
];

/** One row per enrolled user: status, progress, per-activity counts, links, dates. */
export function enrollmentRows(enrollments: CampaignEnrollment[]): Record<string, string | number>[] {
  return enrollments.map((e) => {
    const c = e.progress?.counts;
    return {
      User: e.user_email ?? `User #${e.user}`,
      Status: statusLabel(e.status),
      'Progress %': enrollmentPercent(e),
      'Steps done': c ? `${c.completed}/${c.total}` : '—',
      'Journey maps': c?.journey_maps ?? '—',
      Assessments: c?.assessments ?? '—',
      Account: e.customer ?? '—',
      Opportunity: e.opportunity ?? '—',
      Started: fmtDate(e.started_at),
      Completed: fmtDate(e.completed_at),
    };
  });
}

// ---------------------------------------------------------------------------
// Per-step aggregation — the delivery side of a campaign
// ---------------------------------------------------------------------------

/** Count of step statuses in each status, canonical order — a per-step status breakdown. */
export function stepStatusDistribution(statuses: CampaignDeliveryStepStatus[]): MetricRow[] {
  const counts = new Map<StepStatus, number>(STEP_STATUSES.map((s) => [s.value, 0]));
  statuses.forEach((s) => {
    if (counts.has(s.status)) counts.set(s.status, (counts.get(s.status) ?? 0) + 1);
  });
  return STEP_STATUSES.map((s) => ({ label: s.label, value: counts.get(s.value) ?? 0 }));
}

/** Column order for the per-step completion datagrid (across a campaign's deliveries). */
export const STEP_COMPLETION_COLUMNS: string[] = [
  'Step',
  'Kind',
  'Deliveries',
  'Completed',
  'In Progress',
  'Not Started',
  'Skipped',
  'Completion %',
];

/**
 * One row per non-group step, aggregating that step's status across every delivery in the campaign.
 * `steps` gives the canonical order and names; `statuses` are the per-delivery `CampaignDeliveryStepStatus`
 * rows (fanned out over the campaign's deliveries). Steps with no per-delivery status (e.g. one-time
 * enablement steps tracked on the enrollment) still appear, at 0 — "all steps" visibility.
 *
 * **Hidden steps are excluded** (via `visibleSteps`, which also drops the subtree of a hidden group):
 * a step the runner would never show must not count toward completion metrics.
 */
export function stepCompletionRows(
  steps: CampaignStep[],
  statuses: CampaignDeliveryStepStatus[]
): Record<string, string | number>[] {
  const byStep = new Map<number, CampaignDeliveryStepStatus[]>();
  statuses.forEach((s) => {
    const list = byStep.get(s.step) ?? [];
    list.push(s);
    byStep.set(s.step, list);
  });

  const ordered = visibleSteps(steps)
    .filter((s) => s.step_kind !== 'group')
    .sort((a, b) => a.order - b.order);

  return ordered.map((step) => {
    const rows = byStep.get(step.id) ?? [];
    const count = (st: StepStatus) => rows.filter((r) => r.status === st).length;
    const done = count('completed');
    const denom = rows.length;
    return {
      Step: step.name,
      Kind: stepKindLabel(step.step_kind),
      Deliveries: denom,
      Completed: done,
      'In Progress': count('in_progress'),
      'Not Started': count('not_started'),
      Skipped: count('skipped'),
      'Completion %': denom > 0 ? Math.round((done / denom) * 100) : 0,
    };
  });
}

/** Per-step completion percent, tile-ready — a horizontal bar across the campaign's steps. */
export function stepCompletion(
  steps: CampaignStep[],
  statuses: CampaignDeliveryStepStatus[]
): MetricRow[] {
  return stepCompletionRows(steps, statuses).map((r) => ({
    label: String(r.Step),
    value: Number(r['Completion %']),
  }));
}

// ---------------------------------------------------------------------------
// Delivery-detail aggregation — the leaf level (one delivery)
// ---------------------------------------------------------------------------

/** Column order for the per-delivery datagrid within a campaign. */
export const DELIVERY_COLUMNS: string[] = [
  'Delivery',
  'Status',
  'Owner',
  'Audience',
  'Scheduled',
  'Completed',
];

/** One row per delivery in a campaign: status, owner, audience mode, dates. */
export function deliveryRows(deliveries: CampaignDelivery[]): Record<string, string | number>[] {
  return deliveries.map((d) => ({
    Delivery: d.name ?? `Delivery #${d.id}`,
    Status: deliveryStatusLabel(d.status),
    Owner: d.owner ?? '—',
    Audience: d.audience_mode === 'event' ? 'Event' : 'Single customer',
    Scheduled: fmtDate(d.scheduled_for),
    Completed: fmtDate(d.completed_at),
  }));
}

/** Column order for one delivery's per-step status datagrid. */
export const STEP_STATUS_COLUMNS: string[] = ['Step', 'Kind', 'Status', 'Completed'];

/** One row per step status of a single delivery — the deepest step-level detail. */
export function stepStatusRows(
  statuses: CampaignDeliveryStepStatus[]
): Record<string, string | number>[] {
  return statuses.map((s) => ({
    Step: s.step_name ?? `Step #${s.step}`,
    Kind: stepKindLabel(s.step_kind),
    Status: stepStatusLabel(s.status),
    Completed: fmtDate(s.completed_at),
  }));
}

/** Column order for a delivery's customer-target datagrid. */
export const TARGET_COLUMNS: string[] = ['Account', 'Opportunity', 'Status'];

/** One row per customer target of a delivery. */
export function targetRows(targets: CampaignDeliveryTarget[]): Record<string, string | number>[] {
  return targets.map((t) => ({
    Account: t.customer_name ?? t.customer ?? '—',
    Opportunity: t.opportunity ?? '—',
    Status: statusLabel(t.status),
  }));
}

/** Column order for a delivery's action-item datagrid. */
export const ACTION_ITEM_COLUMNS: string[] = ['Item', 'Kind', 'Owner', 'Due', 'Status'];

/** One row per action item / follow-up captured against a delivery. */
export function actionItemRows(items: CampaignActionItem[]): Record<string, string | number>[] {
  return items.map((a) => ({
    Item: a.text,
    Kind: a.kind === 'followup' ? 'Follow-up' : 'Action item',
    Owner: a.owner ?? '—',
    Due: fmtDate(a.due_date),
    Status: actionItemStatusLabel(a.status),
  }));
}

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

/**
 * The always-present, campaign-agnostic analytics. Every campaign has deliveries with a status and a
 * created date, so these apply to all campaign types. Anything campaign-type-specific is registered
 * separately (see `registerCampaignAnalytics`).
 */
export const CORE_ANALYTICS_SECTION: AnalyticsSection = {
  key: 'core',
  tiles: [
    {
      key: 'kpis',
      title: 'At a glance',
      chartType: 'metricchart',
      span: 'full',
      height: 120,
      compute: (ctx) => programKpis(ctx.campaigns, ctx.deliveries),
      // Program-wide only; a single campaign uses the campaign-scoped KPI tile instead.
      visible: (ctx) => ctx.campaignId == null,
    },
    {
      key: 'status',
      title: 'Deliveries by status',
      chartType: 'vertical_bar_chart',
      height: 280,
      compute: (ctx) => statusDistribution(ctx.deliveries),
      visible: (ctx) => ctx.deliveries.length > 0,
    },
    {
      key: 'trend',
      title: 'Deliveries created over time',
      chartType: 'line_chart',
      height: 280,
      compute: (ctx) => deliveryTrend(ctx.deliveries),
      visible: (ctx) => deliveryTrend(ctx.deliveries).length > 0,
    },
    {
      key: 'by-campaign',
      title: 'By campaign',
      chartType: 'datagrid',
      span: 'full',
      compute: (ctx) => perCampaignRows(ctx.campaigns, ctx.deliveries),
      settings: () => ({ selectedColumns: PER_CAMPAIGN_COLUMNS }),
      // The per-campaign breakdown only earns its space in the program-wide view.
      visible: (ctx) => ctx.campaignId == null && ctx.campaigns.length > 0,
    },
  ],
};

/**
 * The campaign-scoped summary — the visualized header of a single campaign's drill-down. Reads the
 * enablement (enrollments) and delivery (step-status) sources supplied on `ctx.extra` by the campaign
 * panel; each tile hides itself until its source is present, so an un-fetched or empty campaign
 * degrades to just the KPI row rather than empty charts.
 */
export const CAMPAIGN_SUMMARY_SECTION: AnalyticsSection = {
  key: 'campaign-summary',
  scopes: ['campaign'],
  tiles: [
    {
      key: 'camp-kpis',
      title: 'At a glance',
      chartType: 'metricchart',
      span: 'full',
      height: 120,
      compute: (ctx) =>
        campaignKpis((ctx.extra?.enrollments as CampaignEnrollment[]) ?? [], ctx.deliveries),
    },
    {
      key: 'enroll-status',
      title: 'Learners by status',
      chartType: 'donut_chart',
      height: 280,
      compute: (ctx) =>
        enrollmentStatusDistribution((ctx.extra?.enrollments as CampaignEnrollment[]) ?? []),
      visible: (ctx) => ((ctx.extra?.enrollments as any[])?.length ?? 0) > 0,
    },
    {
      key: 'step-completion',
      title: 'Step completion across deliveries',
      chartType: 'horizontal_bar_chart',
      span: 'full',
      height: 360,
      compute: (ctx) =>
        stepCompletion(
          (ctx.extra?.steps as CampaignStep[]) ?? [],
          (ctx.extra?.stepStatuses as CampaignDeliveryStepStatus[]) ?? []
        ),
      visible: (ctx) => ((ctx.extra?.stepStatuses as any[])?.length ?? 0) > 0,
    },
  ],
};

/** Live registry. Core first; registrations append. */
const SECTIONS: AnalyticsSection[] = [CORE_ANALYTICS_SECTION, CAMPAIGN_SUMMARY_SECTION];

/**
 * Register an analytics section contributed by a campaign type (or any feature). Idempotent by
 * `key` — re-registering the same key replaces it, so a module can be imported more than once
 * without duplicating tiles. Call at module load from a campaign's own code (e.g. the frontier
 * runner registering workshop-specific rollups).
 */
export function registerCampaignAnalytics(section: AnalyticsSection): void {
  const idx = SECTIONS.findIndex((s) => s.key === section.key);
  if (idx >= 0) SECTIONS[idx] = section;
  else SECTIONS.push(section);
}

/** True if a section applies to the given scope and to at least one campaign in context. */
function sectionApplies(section: AnalyticsSection, scope: AnalyticsScope, ctx: AnalyticsContext): boolean {
  if (section.scopes && !section.scopes.includes(scope)) return false;
  if (!section.runnerKeys) return true;
  const wanted = new Set(section.runnerKeys);
  return ctx.campaigns.some((c) => wanted.has(c.runner_key));
}

/**
 * Resolve the sections to render for a dashboard: filters the registry by scope + runner match, and
 * drops individual tiles whose `visible(ctx)` is false. The host maps the result to `CampaignTile`s.
 */
export function getAnalyticsSections(scope: AnalyticsScope, ctx: AnalyticsContext): AnalyticsSection[] {
  return SECTIONS.filter((section) => sectionApplies(section, scope, ctx))
    .map((section) => ({
      ...section,
      tiles: section.tiles.filter((t) => (t.visible ? t.visible(ctx) : true)),
    }))
    .filter((section) => section.tiles.length > 0);
}

/** Settings for a tile, defaulting to the `{ labelField, valueField }` chart contract. */
export function tileSettings(spec: AnalyticsTileSpec, ctx: AnalyticsContext): Record<string, any> {
  return spec.settings ? spec.settings(ctx) : DEFAULT_CHART_SETTINGS;
}
